import React, { useEffect, useMemo, useRef, useState } from "react";
import { Routes, Route, useLocation, useNavigate, useParams } from "react-router-dom";
import "./Browse.css";

// Read-only watched list. Data: nightly static snapshot in S3 (public prefix imdb/).
const DATA_URL = "https://watchwithtoonss3.s3.af-south-1.amazonaws.com/imdb/watched.json";
const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WD_RANK = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };

const weekday = (s) => {
  if (!s) return "";
  const [y, m, d] = s.split("-").map(Number);
  return y ? WD[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] : "";
};

const COLS = [
  { key: "position", label: "#" },
  { key: "isBadMovie", label: "Bad" },
  { key: "title", label: "Title" },
  { key: "year", label: "Year" },
  { key: "titleType", label: "Type" },
  { key: "imdbRating", label: "IMDb" },
  { key: "runtimeMins", label: "Min" },
  { key: "voteCount", label: "Votes" },
  { key: "yourRating", label: "Yours" },
  { key: "created", label: "Added" },
  { key: "weekday", label: "Wkday" },
];

const EMPTY = { q: "", director: "", genre: "", type: "", yearMin: "", imdbMin: "", minMin: "", votesMin: "", yourMin: "", addedFrom: "", wd: "", bad: "" };

function Filter({ label, children }) {
  return (
    <label className="fitem">
      <span className="flabel">{label}</span>
      {children}
    </label>
  );
}

function TableView({ rows, onOpen, onTopCredits }) {
  // filter/sort state lives here; this component stays mounted so back-navigation restores it
  const [f, setF] = useState(EMPTY);
  const [sort, setSort] = useState({ key: "created", dir: "desc" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleSort = (k) =>
    setSort((p) => (p.key === k ? { key: k, dir: p.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" }));
  const anyFilter = Object.values(f).some((v) => v !== "");
  const types = useMemo(() => Array.from(new Set(rows.map((r) => r.titleType).filter(Boolean))).sort(), [rows]);
  const genres = useMemo(
    () => Array.from(new Set(rows.flatMap((r) => (r.genres || "").split(", ")).filter(Boolean))).sort(),
    [rows]
  );

  const out = useMemo(() => {
    let a = rows;
    const has = (s) => s !== "";
    if (has(f.q)) { const q = f.q.toLowerCase(); a = a.filter((r) => r.title.toLowerCase().includes(q) || (r.originalTitle || "").toLowerCase().includes(q) || (r.people || "").toLowerCase().includes(q)); }
    if (has(f.director)) { const d = f.director.toLowerCase(); a = a.filter((r) => (r.directors || "").toLowerCase().includes(d)); }
    if (has(f.genre)) a = a.filter((r) => (r.genres || "").split(", ").includes(f.genre));
    if (has(f.type)) a = a.filter((r) => r.titleType === f.type);
    if (has(f.yearMin)) a = a.filter((r) => (+r.year || 0) >= +f.yearMin);
    if (has(f.imdbMin)) a = a.filter((r) => (r.imdbRating ?? -1) >= +f.imdbMin);
    if (has(f.minMin)) a = a.filter((r) => (r.runtimeMins ?? 0) >= +f.minMin);
    if (has(f.votesMin)) a = a.filter((r) => (r.voteCount ?? 0) >= +f.votesMin);
    if (has(f.yourMin)) a = a.filter((r) => (+r.yourRating || 0) >= +f.yourMin);
    if (has(f.addedFrom)) a = a.filter((r) => (r.created || "") >= f.addedFrom);
    if (has(f.wd)) a = a.filter((r) => r.weekday === f.wd);
    if (has(f.bad)) a = a.filter((r) => (f.bad === "1" ? r.isBadMovie : !r.isBadMovie));
    const m = sort.dir === "asc" ? 1 : -1;
    const val = (r) => {
      const v = r[sort.key];
      if (sort.key === "weekday") return WD_RANK[v] ?? 9;
      if (sort.key === "year" || sort.key === "yourRating") return v === "" || v == null ? -Infinity : +v;
      return v == null ? -Infinity : v;
    };
    return [...a].sort((x, y) => {
      if (sort.key === "title" || sort.key === "titleType" || sort.key === "created")
        return m * String(x[sort.key] || "").localeCompare(String(y[sort.key] || ""));
      const a0 = val(x), b0 = val(y);
      return m * (a0 < b0 ? -1 : a0 > b0 ? 1 : 0);
    });
  }, [rows, f, sort]);

  const arrow = (k) => (sort.key === k ? (sort.dir === "asc" ? " ▲" : " ▼") : "");
  const num = (k, label, step) => (
    <Filter label={label}><input type="number" step={step} value={f[k]} onChange={(e) => set(k, e.target.value)} /></Filter>
  );

  return (
    <div>
      <div className="headbar"><h1>My movies <span className="dim">({rows.length})</span></h1>{onTopCredits && <button className="linky" onClick={onTopCredits}>top credits →</button>}</div>
      <div className="filterbar">
        <Filter label="Search"><input className="search" placeholder="title, director, cast…" value={f.q} onChange={(e) => set("q", e.target.value)} /></Filter>
        <Filter label="Director"><input value={f.director} onChange={(e) => set("director", e.target.value)} placeholder="any" /></Filter>
        <Filter label="Genre">
          <select value={f.genre} onChange={(e) => set("genre", e.target.value)}>
            <option value="">all</option>{genres.map((t) => <option key={t}>{t}</option>)}
          </select>
        </Filter>
        <Filter label="Type">
          <select value={f.type} onChange={(e) => set("type", e.target.value)}>
            <option value="">all</option>{types.map((t) => <option key={t}>{t}</option>)}
          </select>
        </Filter>
        {num("yearMin", "Year ≥")}
        {num("imdbMin", "IMDb ≥", "0.1")}
        {num("minMin", "Min ≥")}
        {num("votesMin", "Votes ≥")}
        {num("yourMin", "Yours ≥")}
        <Filter label="Added from"><input type="date" value={f.addedFrom} onChange={(e) => set("addedFrom", e.target.value)} /></Filter>
        <Filter label="Wkday">
          <select value={f.wd} onChange={(e) => set("wd", e.target.value)}>
            <option value="">any</option>{Object.keys(WD_RANK).map((d) => <option key={d}>{d}</option>)}
          </select>
        </Filter>
        <Filter label="Bad">
          <select value={f.bad} onChange={(e) => set("bad", e.target.value)}>
            <option value="">any</option><option value="1">bad only</option><option value="0">good only</option>
          </select>
        </Filter>
        {anyFilter && <button className="linky" onClick={() => setF(EMPTY)}>✕ clear all</button>}
      </div>
      <div className="dim small">{out.length} of {rows.length} shown</div>
      <div className="tblwrap">
        <table>
          <thead>
            <tr>{COLS.map((c) => <th key={c.key} onClick={() => toggleSort(c.key)}>{c.label}{arrow(c.key)}</th>)}</tr>
          </thead>
          <tbody>
            {out.map((r) => (
              <tr key={r.titleId} onClick={() => onOpen(r.titleId)} className="rowlink">
                <td>{r.position}</td>
                <td>{r.isBadMovie ? <span className="badtag" title="bad movie">●</span> : ""}</td>
                <td className="titlecell">
                  {r.title}
                  {r.originalTitle && r.originalTitle !== r.title && <span className="dim"> ({r.originalTitle})</span>}
                  {(r.directors || r.topCast) && <div className="dim small">{r.directors && `dir. ${r.directors}`}{r.directors && r.topCast && "  "}{r.topCast && `cast: ${r.topCast}`}</div>}
                </td>
                <td>{r.year}</td>
                <td>{r.titleType}</td>
                <td>{r.imdbRating ?? ""}</td>
                <td>{r.runtimeMins ?? ""}</td>
                <td>{r.voteCount ?? ""}</td>
                <td>{r.yourRating || ""}</td>
                <td>{r.created}</td>
                <td>{r.weekday}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Crew({ list, appearances, onPerson }) {
  const groups = {};
  for (const c of list) (groups[c.category || "Other"] ??= []).push(c);
  const first = ["Actor", "Actress", "Director", "Producer"];
  const cats = first.concat(Object.keys(groups).filter((g) => !first.includes(g))).filter((c) => groups[c]);
  return (
    <div className="crew">
      {cats.map((cat) => (
        <div key={cat} className="crewblock">
          <h3>{cat} <span className="dim">({groups[cat].length})</span></h3>
          <ul>
            {groups[cat].map((c, i) => (
              <li key={i}>
                <button className="personlink" onClick={() => onPerson(c.nameId)}>
                  {c.name}
                  {(appearances[c.nameId] || 0) > 1 && <span className="dim"> ({appearances[c.nameId]})</span>}
                </button>
                {c.charText && <span className="dim"> — {c.charText}</span>}
                {c.roleText && cat !== "Actor" && cat !== "Actress" && <span className="dim"> ({c.roleText})</span>}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function MovieDetail({ data, onPerson }) {
  const { id } = useParams();
  const nav = useNavigate();
  const m = data.byId[id];
  if (!m) return <div className="err">Title not found.</div>;
  const cr = (data.creditsByTitle[id] || []).map((c) => ({ ...c, name: data.people[c.nameId]?.[0] || c.nameId }));
  return (
    <div className="detail">
      <button className="linky" onClick={() => nav(-1)}>← back</button>
      <h1>{m.title} <span className="dim">({m.year})</span></h1>
      {m.originalTitle && m.originalTitle !== m.title && <div className="dim">original: {m.originalTitle}</div>}
      <div className="meta">
        {m.titleType} · IMDb ★ {m.imdbRating ?? "?"} ({m.voteCount ?? 0} votes) · {m.runtimeMins ?? "?"} min
        {m.directors ? <div>🎬 {m.directors}</div> : null}
        <div>genres: {m.genres}</div>
        <div>
          added {m.created}{m.weekday ? ` (${m.weekday})` : ""}
          {m.yourRating ? ` · yours: ${m.yourRating}/10` : ""}
          {m.isBadMovie ? " · 👎 bad movie" : ""}
        </div>
      </div>
      {m.description && <p className="desc dim">{m.description}</p>}
      <a className="linky" href={`https://www.imdb.com/title/${m.titleId}/`} target="_blank" rel="noreferrer">view on IMDb ↗</a>
      {cr.length > 0 && <Crew list={cr} appearances={data.appearances} onPerson={onPerson} />}
    </div>
  );
}

function PersonView({ data, onOpenMovie }) {
  const { id } = useParams();
  const nav = useNavigate();
  const p = data.people[id];
  if (!p) return <div className="err">Person not found.</div>;
  const films = (data.creditsByName[id] || [])
    .map((c) => ({ ...c, t: data.byId[c.titleId] }))
    .filter((c) => c.t)
    .sort((a, b) => (+b.t.year || 0) - (+a.t.year || 0) || a.t.title.localeCompare(b.t.title));
  const cats = {};
  films.forEach((f) => { cats[f.category] = (cats[f.category] || 0) + 1; });
  return (
    <div className="detail">
      <button className="linky" onClick={() => nav(-1)}>← back</button>
      <div className="person-head">
        {p[1] && <img className="avatar" src={p[1]} alt="" />}
        <div>
          <h1>{p[0]}</h1>
          <div className="meta"><b>{films.length}</b> credits in the list</div>
          <div className="dim small">{Object.entries(cats).map(([c, n]) => `${c} (${n})`).join(" · ")}</div>
        </div>
      </div>
      <h2>Movies they were involved in</h2>
      <table className="films">
        <tbody>
          {films.map((f, i) => (
            <tr key={i} className="rowlink" onClick={() => onOpenMovie(f.titleId)}>
              <td className="dim">{f.t.year}</td>
              <td className="titlecell">
                <b>{f.t.title}</b>
                {f.charText && <span className="dim"> — {f.charText}</span>}
                {f.roleText && <span className="dim"> ({f.roleText})</span>}
              </td>
              <td className="dim">{f.category}</td>
              <td>{f.t.imdbRating ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Browse() {
  const nav = useNavigate();
  const [raw, setRaw] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetch(DATA_URL)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setRaw)
      .catch((e) => setErr(`Could not load list: ${e.message}`));
  }, []);

  const data = useMemo(() => {
    if (!raw) return null;
    const creditsByTitle = {}, creditsByName = {};
    const appearances = {}, filmsOf = {};                 // personId -> total credits / distinct films
    const catInfo = {}, catCount = {}, catFilms = {};     // per-category tallies for top credits
    for (const [titleId, nameId, category, roleText, charText] of raw.credits) {
      const c = { titleId, nameId, category, roleText, charText };
      (creditsByTitle[titleId] ??= []).push(c);
      (creditsByName[nameId] ??= []).push(c);
      appearances[nameId] = (appearances[nameId] || 0) + 1;
      (filmsOf[nameId] ??= new Set()).add(titleId);
      (catInfo[category] ??= { credits: 0, people: new Set() });
      catInfo[category].credits++;
      catInfo[category].people.add(nameId);
      (catCount[category] ??= {});
      catCount[category][nameId] = (catCount[category][nameId] || 0) + 1;
      (catFilms[category] ??= {});
      (catFilms[category][nameId] ??= new Set()).add(titleId);
    }
    // rows: scraped-first directors, top-3 cast, search haystack (
    // server-supplied topCast/people win; else computed from credits for pre-Nov snapshots)
    const rows = raw.titles.map((t) => {
      const cr = creditsByTitle[t.titleId] || [];
      const nameOf = (c) => (raw.people[c.nameId] || [])[0];
      const dirNames = cr.filter((c) => c.category === "Director").map(nameOf).filter(Boolean);
      const billed = cr.filter((c) => c.category === "Actor" || c.category === "Actress");
      const directors = dirNames.join(", ") || t.directors || "";
      const topCast = t.topCast || billed.slice(0, 3).map(nameOf).filter(Boolean).join(", ");
      const people = (t.people || "").trim()
        || [directors, ...billed.slice(0, 10).map(nameOf)].filter(Boolean).join(" ");
      return { ...t, weekday: weekday(t.created), directors, topCast, people };
    });
    const byId = Object.fromEntries(rows.map((r) => [r.titleId, r]));
    // rank: credits DESC, then person id (same order as the local imdb app)
    const byRank = (a, b) => b.n - a.n || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
    const ranked = Object.keys(appearances).map((id) => ({ id, n: appearances[id], films: filmsOf[id].size })).sort(byRank);
    const catLists = {};
    for (const cat of Object.keys(catCount))
      catLists[cat] = Object.keys(catCount[cat]).map((id) => ({ id, n: catCount[cat][id], films: catFilms[cat][id].size })).sort(byRank);
    const cats = Object.keys(catInfo)
      .map((cc) => ({ category: cc, credits: catInfo[cc].credits, people: catInfo[cc].people.size }))
      .sort((a, b) => b.credits - a.credits);
    return { rows, byId, creditsByTitle, creditsByName, people: raw.people, appearances, ranked, catLists, cats };
  }, [raw]);

  if (err) return <div className="browse"><div className="err">{err}</div></div>;
  if (!data) return <div className="browse"><div className="dim">loading…</div></div>;

  return (
    <div className="browse">
      <Routes>
        <Route path="/movie/:id" element={<MovieDetail data={data} onPerson={(nm) => nav("/name/" + nm)} />} />
        <Route path="/name/:id" element={<PersonView data={data} onOpenMovie={(tt) => nav("/movie/" + tt)} />} />
        <Route path="/topcredits" element={<TopOverview data={data}
          onPerson={(nm) => nav("/name/" + nm)}
          onAll={() => nav("/topcredits/all")}
          onCat={(c) => nav("/topcredits/cat/" + encodeURIComponent(c))}
          onHome={() => nav("/")} />} />
        <Route path="/topcredits/all" element={<TopListPage data={data} title="Everyone" note="(full list, all credits)" list={data.ranked}
          onPerson={(nm) => nav("/name/" + nm)}
          onBack={() => nav("/topcredits")} />} />
        <Route path="/topcredits/cat/:cat" element={<TopCatPage data={data}
          onPerson={(nm) => nav("/name/" + nm)}
          onBack={() => nav("/topcredits")} />} />
        <Route path="*" element={null} />
      </Routes>
      {/* list stays mounted (hidden on detail pages) so filters, sort and scroll survive back-navigation */}
      <ListHost rows={data.rows} onOpen={(tt) => nav("/movie/" + tt)} onTopCredits={() => nav("/topcredits")} />
    </div>
  );
}

// one ranked-person line: rank., name (link), (credit count), films
function People({ names, list, onPerson }) {
  return (
    <ul>
      {list.map((r, i) => (
        <li key={r.id}>
          <span className="dim small">{i + 1}.</span>{" "}
          <button className="personlink" onClick={() => r.id && onPerson(r.id)}>{(names[r.id] || [r.id])[0]}</button>{" "}
          <span className="dim">({r.n})</span>
          <span className="dim small"> · {r.films} {r.films === 1 ? "film" : "films"}</span>
        </li>
      ))}
    </ul>
  );
}

// /topcredits — overall top 20 + top 20 per category, with full-list links
function TopOverview({ data, onPerson, onAll, onCat, onHome }) {
  return (
    <div className="detail">
      <div className="headbar">
        <h1>Top credits <span className="dim">(top 20 per list)</span></h1>
        <button className="linky" onClick={onHome}>← My movies</button>
      </div>
      <div className="dim small">counts over the whole scraped database — click a name to see their films</div>
      <div className="crew">
        <div className="crewblock">
          <h3>Overall <span className="dim">(top 20 of all people)</span></h3>
          <People names={data.people} list={data.ranked.slice(0, 20)} onPerson={onPerson} />
          <button className="linky" onClick={onAll}>see more — everyone →</button>
        </div>
        {data.cats.map((c) => (
          <div className="crewblock" key={c.category}>
            <h3>{c.category} <span className="dim">({c.people} people · {c.credits} credits)</span></h3>
            <People names={data.people} list={(data.catLists[c.category] || []).slice(0, 20)} onPerson={onPerson} />
            <button className="linky" onClick={() => onCat(c.category)}>see more — everyone in {c.category} →</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// full ranked list for one scope: all people, or one category
function TopListPage({ data, title, note, list, onPerson, onBack }) {
  return (
    <div className="detail">
      <div className="headbar">
        <h1>{title} <span className="dim">{note}</span></h1>
        <button className="linky" onClick={onBack}>← back to top credits</button>
      </div>
      <div className="dim small">counts over the whole scraped database — click a name to see their films</div>
      <div className="crew"><div className="crewblock"><People names={data.people} list={list} onPerson={onPerson} /></div></div>
    </div>
  );
}

// /topcredits/cat/:cat — shows the precomputed list for this category
function TopCatPage({ data, onPerson, onBack }) {
  const { cat } = useParams();
  return <TopListPage data={data} title={`${cat} — everyone`} note="(full list)" list={data.catLists[cat] || []} onPerson={onPerson} onBack={onBack} />;
}

function ListHost({ rows, onOpen, onTopCredits }) {
  const { pathname } = useLocation();
  const isList = !/\/(movie|name|topcredits)\//.test(pathname);
  const scrollY = useRef(0);
  useEffect(() => {
    if (isList) window.scrollTo(0, scrollY.current);
    else window.scrollTo(0, 0);
  }, [isList]);
  return (
    <div style={{ display: isList ? undefined : "none" }}>
      <TableView rows={rows} onOpen={(id) => { scrollY.current = window.scrollY; onOpen(id); }} onTopCredits={onTopCredits} />
    </div>
  );
}
